import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button, ButtonGroup, Checkbox } from '@upmc/ims.component-library';

import Accordion from '../../components/UI/Accordion/Accordion';
import PieChart from '../../components/UI/Infographic/Charts/Pie/PieChart';
import Radio from '../../components/UI/MDC/Radio/Radio';
import * as actions from '../../store/uar/actions';
import * as lifecycleWrapper from './util/lifecycleWrapper';

import classes from './UserAccessReview.module.scss';
import UARLayout from './containers/Layout';
import { UAR_CONSTANTS } from './util/constants';
import { prettyDateFormat, concatenateClassNames } from '../../utils';

const Dashboard = props => {
	/**
	 * Extract needed variables from the props provided by the `Scaffold`
	 */
	const {
		dispatch,
		loggedInUser,
		error,
		errorMessage,
		loading,
		userStaff,
		bulkSelections,
	} = props;

	const expiringSoonThreshold = 90;

	/**
	 * aggregate state for the page
	 * @todo break this down into individual useState()s
	 */
	const [uarState, setUarState] = useState({
		staff: [],
		displayFilters: {
			open: false,
			name: '',
			employee: false,
			nonEmployee: false,
			contingentWorker: false,
			expired: false,
			expiringSoon: false,
			delegated: false,
			sortCategory: 'lastName',
			sortDirection: 'asc',
		},
		contingentWorkerCount: {
			count: 0,
			delinquent: 0,
			valid: 0,
		},
		employeeCount: {
			count: 0,
			delinquent: 0,
			valid: 0,
		},
		nonEmployeeCount: {
			count: 0,
			delinquent: 0,
			valid: 0,
		},
		count: 0,
	});

	/**
	 * shortcut pointers to (set)UarState
	 */
	const {
		staff,
		displayFilters,
		contingentWorkerCount,
		employeeCount,
		nonEmployeeCount,
	} = uarState;

	const singleButtons = [
		{
			children: 'Review Employee Access',
			path: '/UserAccessReview/Validate',
			displayFor: [UAR_CONSTANTS.EMPLOYEE_KEY],
		},
		{
			children: 'Renew Non-Employee Access',
			path: '/UserAccessReview/Renew',
			displayFor: [
				UAR_CONSTANTS.NON_EMPLOYEE_KEY,
				UAR_CONSTANTS.CONTINGENT_WORKER_KEY,
			],
		},
		{
			children: 'More Info',
			path: '/UserAccessReview/AccountDetails',
			displayFor: [
				UAR_CONSTANTS.NON_EMPLOYEE_KEY,
				UAR_CONSTANTS.EMPLOYEE_KEY,
				UAR_CONSTANTS.CONTINGENT_WORKER_KEY,
			],
		},
		{
			children: 'View Org Chart',
			path: '/UserAccessReview/OrgChart',
			displayFor: [
				UAR_CONSTANTS.NON_EMPLOYEE_KEY,
				UAR_CONSTANTS.EMPLOYEE_KEY,
				UAR_CONSTANTS.CONTINGENT_WORKER_KEY,
			],
		},
		{
			children: 'Reassign',
			path: '/UserAccessReview/Reassign',
			displayFor: [UAR_CONSTANTS.NON_EMPLOYEE_KEY],
		},
		{
			children: 'Terminate',
			path: '/UserAccessReview/Terminate',
			displayFor: [
				UAR_CONSTANTS.NON_EMPLOYEE_KEY,
				UAR_CONSTANTS.EMPLOYEE_KEY,
				UAR_CONSTANTS.CONTINGENT_WORKER_KEY,
			],
		},
		{
			children: 'Delegate',
			path: '/UserAccessReview/Delegate',
			displayFor: [
				UAR_CONSTANTS.NON_EMPLOYEE_KEY,
				UAR_CONSTANTS.EMPLOYEE_KEY,
				UAR_CONSTANTS.CONTINGENT_WORKER_KEY,
			],
		},
	];

	/**
	 * (set)BulkButtons - button label and redirect path for bulk action buttons
	 */
	const bulkButtons = [
		{
			label: 'Review All Selected Employees',
			path: '/UserAccessReview/Validate',
		},
		{
			label: 'Reassign All Selected Non-Employees',
			path: '/UserAccessReview/Reassign',
		},
		{
			label: 'Renew All Selected Non-Employees',
			path: '/UserAccessReview/Renew',
		},
	];

	/**
	 * watch for data from API regarding staff
	 */
	useEffect(() => {
		if (userStaff) {
			const newUserStaff = { ...userStaff };
			newUserStaff.staff.forEach(member => {
				member.display = true;
			});
			setUarState({ ...uarState, ...newUserStaff });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userStaff]);

	/**
	 * handle searching for staff
	 * @param {*} event
	 */
	const handleSearch = event => {
		let newDisplayFilters = { ...displayFilters };
		newDisplayFilters.name = event.target.value;
		setUarState({ ...uarState, displayFilters: newDisplayFilters });
	};

	/**
	 *
	 * @param {*} event
	 * @param {*} id
	 */
	const handleFilter = (event, id) => {
		let newDisplayFilters = { ...displayFilters };
		// switch (event.target.id) {
		switch (id) {
			case 'filterEmployee':
				newDisplayFilters.employee = event.target.checked;
				break;
			case 'filterNonEmployee':
				newDisplayFilters.nonEmployee = event.target.checked;
				break;
			case 'filterContingentWorker':
				newDisplayFilters.contingentWorker = event.target.checked;
				break;
			case 'filterExpired':
				newDisplayFilters.expired = event.target.checked;
				break;
			case 'filterExpiringSoon':
				newDisplayFilters.expiringSoon = event.target.checked;
				break;
			default:
				break;
		}
		// newDisplayFilters.name = event.target.value;
		setUarState({ ...uarState, displayFilters: newDisplayFilters });
	};

	useEffect(() => {
		if (staff) {
			const newStaff = [...staff];
			newStaff.forEach(member => {
				let display = true;
				let displaySearch = false,
					isSearchFiltered = false;
				let displayFilter = false,
					isTypeFiltered = false;
				let displayExpired = false,
					isExpiredFiltered = false;
				let displayExpiring = false,
					isExpiringFiltered = false;
				//search
				if (
					uarState.displayFilters.name &&
					uarState.displayFilters.name.length > 0
				) {
					displaySearch =
						displaySearch ||
						member.firstName
							.toLowerCase()
							.includes(
								uarState.displayFilters.name.toLowerCase(),
							) ||
						member.lastName
							.toLowerCase()
							.includes(
								uarState.displayFilters.name.toLowerCase(),
							) ||
						member.middleInitial
							.toLowerCase()
							.includes(
								uarState.displayFilters.name.toLowerCase(),
							);
					isSearchFiltered = true;
				}
				//staff types
				if (uarState.displayFilters.employee) {
					displayFilter =
						displayFilter ||
						member.type.toLowerCase() ===
							UAR_CONSTANTS.EMPLOYEE_KEY.toLowerCase();
					isTypeFiltered = true;
				}
				if (uarState.displayFilters.nonEmployee) {
					displayFilter =
						displayFilter ||
						member.type.toLowerCase() ===
							UAR_CONSTANTS.NON_EMPLOYEE_KEY.toLowerCase();
					isTypeFiltered = true;
				}
				if (uarState.displayFilters.contingentWorker) {
					displayFilter =
						displayFilter ||
						member.type.toLowerCase() ===
							UAR_CONSTANTS.CONTINGENT_WORKER_KEY.toLowerCase();
					isTypeFiltered = true;
				}
				//expired
				if (uarState.displayFilters.expired) {
					displayExpired = displayExpired || member.isExpired;
					isExpiredFiltered = true;
				}
				//expiring
				if (uarState.displayFilters.expiringSoon) {
					const now = new Date();
					let threshold = new Date();
					threshold.setDate(now.getDate() + expiringSoonThreshold);
					const dueSoon = !member.isExpired
						? Date.parse(member.dateValidationDue)
							? Date.parse(member.dateValidationDue) > now &&
								Date.parse(member.dateValidationDue) < threshold
							: false
						: false;
					displayExpiring = displayExpiring || dueSoon;
					isExpiringFiltered = true;
				}

				display =
					display &&
					(isSearchFiltered ? displaySearch : true) &&
					(isTypeFiltered ? displayFilter : true) &&
					(isExpiredFiltered ? displayExpired : true) &&
					(isExpiringFiltered ? displayExpiring : true);

				member.display = display;
				if (!display) {
					member.selected = false;
					dispatch(
						actions.removeBulkSelection({
							payload: member,
						}),
					);
				}
			});
			setUarState({ ...uarState, staff: newStaff });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [displayFilters]);

	/**
	 *
	 * @param {*} event
	 * @param {*} id
	 */
	const handleSort = (event, id) => {
		let newDisplayFilters = { ...displayFilters };
		// switch (event.target.id) {
		switch (id) {
			case 'sortCategory':
				newDisplayFilters.sortCategory = event.target.value;
				break;
			case 'sortDirectionAsc':
				newDisplayFilters.sortDirection = 'asc';
				break;
			case 'sortDirectionDesc':
				newDisplayFilters.sortDirection = 'desc';
				break;
			default:
				break;
		}
		setUarState({ ...uarState, displayFilters: newDisplayFilters });
	};

	/**
	 *
	 */
	const compareStaff = (a, b) => {
		const sortCategory = displayFilters.sortCategory;
		const direction = displayFilters.sortDirection === 'desc' ? -1 : 1;

		const criteriaA = Date.parse(a[sortCategory])
			? Date.parse(a[sortCategory])
			: a[sortCategory];
		const criteriaB = Date.parse(b[sortCategory])
			? Date.parse(b[sortCategory])
			: b[sortCategory];

		let comparison = 0;
		if (criteriaA > criteriaB) {
			comparison = 1;
		} else if (criteriaA < criteriaB) {
			comparison = -1;
		}

		return direction * comparison;
	};

	/**
	 *
	 */
	const addBulkSelection = (event, member) => {
		let addNew = event.target.checked;
		//local state

		let newStaff = [...staff];
		newStaff.forEach(mem => {
			if (mem.esoFiId === member.esoFiId) {
				mem.selected = !mem.selected;
			}
		});

		setUarState({
			...uarState,
			staff: newStaff,
		});

		//global store
		if (addNew) {
			dispatch(
				actions.addBulkSelection({
					payload: member,
				}),
			);
		} else {
			dispatch(
				actions.removeBulkSelection({
					payload: member,
				}),
			);
		}
	};

	/**
	 *
	 * @param {*} event
	 */
	const deselectAllBulkSelection = event => {
		let newStaff = [...staff];
		newStaff.forEach(member => {
			member.selected = false;
		});
		setUarState({ ...uarState, staff: newStaff });
		dispatch(actions.resetBulkSelection());
	};

	/**
	 *
	 * @param {*} event
	 * @param {*} select
	 */
	const selectAllBulkSelection = (event, select) => {
		deselectAllBulkSelection();

		if (select) {
			let newStaff = [...staff];
			newStaff.forEach(member => {
				if (member.display) {
					//update the master staff list if they have been selected via filter
					let newStaffResult = newStaff.find(
						m => m.esoFiId === member.esoFiId,
					);
					if (newStaffResult) {
						newStaffResult.selected = true;
					}

					dispatch(
						actions.addBulkSelection({
							payload: member,
						}),
					);
				}
			});
			setUarState({
				...uarState,
				staff: newStaff,
			});
		}
	};

	/**
	 *
	 * @param {*} event
	 * @param {*} filterType
	 */
	const setFilter = (event, filterType) => {
		let newDisplayFilters = {
			open: false,
			name: '',
			employee: false,
			nonEmployee: false,
			contingentWorker: false,
			expired: false,
			expiringSoon: false,
			delegated: false,
			sortCategory: 'lastName',
			sortDirection: 'asc',
		};

		switch (filterType) {
			case UAR_CONSTANTS.EMPLOYEE_KEY:
				newDisplayFilters.employee = true;
				newDisplayFilters.open = true;
				break;
			case UAR_CONSTANTS.NON_EMPLOYEE_KEY:
				newDisplayFilters.nonEmployee = true;
				newDisplayFilters.open = true;
				break;
			case UAR_CONSTANTS.CONTINGENT_WORKER_KEY:
				newDisplayFilters.contingentWorker = true;
				newDisplayFilters.open = true;
				break;
			default:
				newDisplayFilters.open = !displayFilters.open;
				break;
		}
		setUarState({ ...uarState, displayFilters: newDisplayFilters });
	};

	/**
	 *
	 * @param {*} type
	 */
	const getPie = type => {
		let data = {};
		let percentValid,
			delinquentCount,
			validatedCount = 0;
		let title = '';

		switch (type) {
			case UAR_CONSTANTS.EMPLOYEE_KEY:
				title = 'Employees';
				validatedCount = employeeCount.valid;
				delinquentCount = employeeCount.delinquent;
				percentValid =
					employeeCount.count > 0
						? Math.floor(
								(employeeCount.valid / employeeCount.count) *
									100,
							)
						: 0;
				data = [
					{
						label: 'Validated',
						value: validatedCount,
						color: 'green',
					},
					{ label: 'Past Due', value: delinquentCount, color: 'red' },
				];
				break;
			case UAR_CONSTANTS.NON_EMPLOYEE_KEY:
				title = 'Non-Employees';
				validatedCount = nonEmployeeCount.valid;
				delinquentCount = nonEmployeeCount.delinquent;
				percentValid =
					nonEmployeeCount.count > 0
						? Math.floor(
								(nonEmployeeCount.valid /
									nonEmployeeCount.count) *
									100,
							)
						: 0;
				data = [
					{
						label: 'Validated',
						value: validatedCount,
						color: 'green',
					},
					{ label: 'Past Due', value: delinquentCount, color: 'red' },
				];
				break;
			case UAR_CONSTANTS.CONTINGENT_WORKER_KEY:
				title = 'Contingent Workers';
				validatedCount = contingentWorkerCount.valid;
				delinquentCount = contingentWorkerCount.delinquent;
				percentValid =
					contingentWorkerCount.count > 0
						? Math.floor(
								(contingentWorkerCount.valid /
									contingentWorkerCount.count) *
									100,
							)
						: 0;
				data = [
					{
						label: 'Validated',
						value: validatedCount,
						color: 'green',
					},
					{ label: 'Past Due', value: delinquentCount, color: 'red' },
				];
				break;
			default:
				title = 'No Data';
				data = [{ label: 'No Data', value: 1, color: 'gray' }];
				break;
		}
		let pie = (
			<PieChart
				width='275'
				height='275'
				data={data}
				options={{
					isDonut: true,
					donutLabel:
						delinquentCount + validatedCount === 0
							? 'N/A'
							: percentValid + '%',
					title: title,
					legend: true,
					className: classes['pie-chart'],
				}}
			/>
		);
		return pie;
	};

	//styles
	let pieContainerStyles = [];
	pieContainerStyles.push(classes['container']);
	pieContainerStyles.push(classes['pie-container']);
	const pieContainerStyle = pieContainerStyles.join(' ');

	let resultContainerStyles = [];
	resultContainerStyles.push(classes['results-container']);
	const resultContainerStyle = resultContainerStyles.join(' ');

	let listContainerStyles = [];
	listContainerStyles.push(classes['container']);
	listContainerStyles.push(classes['list-container']);
	listContainerStyles.push(classes['list-container--scrollable']);
	const listContainerStyle = listContainerStyles.join(' ');

	//components
	const staffList =
		staff.length > 0 ? (
			staff.filter(member => member.display === true).length > 0 ? (
				staff.sort(compareStaff).map(member => {
					return member.display ? (
						<div
							className={classes['user-row']}
							key={member.esoFiId}
						>
							<div className={classes['user-details']}>
								<div className={classes['user-detail-select']}>
									<Checkbox
										name={'bulk_' + member.esoFiId}
										label='Select account for bulk action'
										checked={member.selected}
										disabled={false}
										onChange={e => {
											addBulkSelection(e, member);
										}}
									/>
								</div>
								<div className={classes['user-detail-info']}>
									<div
										className={
											classes[
												'user-detail-info-cell-name'
											]
										}
									>
										<strong>
											{member.lastName},{' '}
											{member.firstName}{' '}
											{member.middleInitial}
										</strong>
										{member.isDelegated
											? member.delegatedOwnerName
												? ` (Delegated from ${member.delegatedOwnerName})`
												: ' (Delegated)'
											: null}
									</div>
								</div>
								<div className={classes['user-detail-info']}>
									<div
										className={
											classes['user-detail-info-cell']
										}
									>
										{lifecycleWrapper.accountTypeLookup(
											member.type,
										)}
									</div>
									<div
										className={
											classes['user-detail-info-cell']
										}
									>
										{member.phoneNumber}
									</div>
									{member.type ===
										UAR_CONSTANTS.EMPLOYEE_KEY && (
										<div
											className={
												classes['user-detail-info-cell']
											}
										>
											Employee ID:{' '}
											{member.employeeId
												? member.employeeId
												: 'N/A'}
										</div>
									)}
									{member.type ===
										UAR_CONSTANTS.NON_EMPLOYEE_KEY && (
										<div
											className={
												classes['user-detail-info-cell']
											}
										>
											Company:{' '}
											{member.nonEmployeeCompanyName
												? member.nonEmployeeCompanyName
												: 'N/A'}
										</div>
									)}
									<div
										className={
											classes['user-detail-info-cell']
										}
									>
										Email: {member.email}
									</div>
									<div
										className={
											classes['user-detail-info-cell']
										}
									>
										NTID: {member.ntid}
									</div>
								</div>
								<div className={classes['user-detail-info']}>
									<div
										className={
											classes['user-detail-info-cell']
										}
									>
										Date Last Validated:{' '}
										{prettyDateFormat(member.dateValidated)}
									</div>
									<div
										className={
											classes['user-detail-info-cell']
										}
									>
										Validation Expiration:{' '}
										{prettyDateFormat(
											member.dateValidationDue,
										)}
									</div>
									<div
										className={
											classes['user-detail-info-cell']
										}
									>
										{member.isExpired ? (
											<span
												className={concatenateClassNames(
													['text--expired'],
													classes,
												)}
											>
												(Expired)
											</span>
										) : null}
									</div>
								</div>
							</div>
							<ButtonGroup className={classes['user-actions']}>
								{singleButtons.map(singleButton => {
									if (
										singleButton.displayFor.includes(
											member.type,
										)
									) {
										return (
											<div
												key={
													'user-action-' +
													singleButton.children
												}
											>
												<Link
													to={{
														pathname:
															singleButton.path,
														search:
															'?esoFiId=' +
															member.esoFiId,
													}}
													className={concatenateClassNames(
														['button__link--reset'],
														classes,
													)}
												>
													<Button variant='outlined'>
														{singleButton.children}
													</Button>
												</Link>
											</div>
										);
									} else {
										return null;
									}
								})}
							</ButtonGroup>
						</div>
					) : null;
				})
			) : (
				<div className={classes['user-row']}>
					<div className={classes['user-detail-info']}>
						<h3 className={classes['center']}>
							Filter yielded no results.
						</h3>
					</div>
				</div>
			)
		) : (
			<div className={classes['user-row']}>
				<div className={classes['user-detail-info']}>
					<h3 className={classes['center']}>
						You do not have any staff to display.
					</h3>
				</div>
			</div>
		);

	const title =
		loggedInUser && loggedInUser.impersonator
			? `${loggedInUser.impersonator.firstName} ${loggedInUser.impersonator.lastName}'s Staff Dashboard`
			: (loggedInUser?.name && loggedInUser.name.split(', ').length > 1
					? loggedInUser.name.split(', ')[1] +
						' ' +
						loggedInUser.name.split(', ')[0] +
						"'s"
					: '') + ' Staff Dashboard';

	return (
		<UARLayout
			title={title}
			loading={loading}
			error={error}
			errorMessage={errorMessage}
			canPerformActions={true}
			help={
				'https://infonet.upmc.com/infonet/ls/content/6236583790140711/annual-user-access-review-guide-pdf-1764799965'
			}
		>
			<div>
				<section id='pie ' className={pieContainerStyle}>
					{employeeCount.count > 0 ? (
						<div
							id='pieEmployee'
							className={classes['pie-chart-cell']}
						>
							<div className={classes['pie-chart-cell-graph']}>
								{getPie(UAR_CONSTANTS.EMPLOYEE_KEY)}
							</div>
							<Button
								onClick={e => {
									setFilter(e, 'employee');
								}}
								variant='text'
								fullWidth={true}
							>
								View Filtered Details Below
							</Button>
						</div>
					) : null}
					{nonEmployeeCount.count > 0 ? (
						<div
							id='pieNonEmployee'
							className={classes['pie-chart-cell']}
						>
							<div className={classes['pie-chart-cell-graph']}>
								{getPie(UAR_CONSTANTS.NON_EMPLOYEE_KEY)}
							</div>
							<Button
								variant='text'
								onClick={e => {
									setFilter(e, 'nonEmployee');
								}}
								fullWidth={true}
							>
								View Filtered Details Below
							</Button>
						</div>
					) : null}
					{contingentWorkerCount.count > 0 ? (
						<div
							id='pieContingentWorker'
							className={classes['pie-chart-cell']}
						>
							<div className={classes['pie-chart-cell-graph']}>
								{getPie(UAR_CONSTANTS.CONTINGENT_WORKER_KEY)}
							</div>
							<Button
								variant='text'
								onClick={e => {
									setFilter(e, 'contingentWorker');
								}}
								fullWidth={true}
							>
								View Filtered Details Below
							</Button>
						</div>
					) : null}
				</section>
				<section id='results' className={resultContainerStyle}>
					{staff.length > 0 ? (
						<Accordion
							label='Filter Results'
							expanded={displayFilters.open}
							onClick={e => {
								setFilter(e, 'open');
							}}
						>
							<div
								id='resultsControls'
								className={classes['results-row']}
							>
								<div id='search' className={classes['search']}>
									<div>
										<strong>Search by name: </strong>
										<input
											type='text'
											value={displayFilters.name}
											onChange={handleSearch}
										/>
									</div>
									<div>
										<strong>Sort By: </strong>
										<select
											id='sortCategory'
											onChange={e =>
												handleSort(e, 'sortCategory')
											}
											defaultValue={
												displayFilters.sortCategory
											}
										>
											<option value='lastName'>
												Last Name
											</option>
											<option value='dateValidationDue'>
												Expiration Date
											</option>
											<option value='type'>
												Staff Type
											</option>
										</select>
										{/* TODO - turn into Radio Buttons*/}
										<Radio
											key='sortDirectionAsc'
											label='Ascending'
											value='Ascending'
											onChange={e =>
												handleSort(
													e,
													'sortDirectionAsc',
												)
											}
											disabled={false}
											checked={
												displayFilters.sortDirection ===
												'asc'
											}
										/>
										<Radio
											key='sortDirectionDesc'
											label='Descending'
											value='Descending'
											onChange={e =>
												handleSort(
													e,
													'sortDirectionDesc',
												)
											}
											disabled={false}
											checked={
												displayFilters.sortDirection ===
												'desc'
											}
										/>
									</div>
								</div>
								<div id='filter' className={classes['filter']}>
									<div>
										<strong>Filter: </strong>
									</div>
									<div>
										<Checkbox
											name='filterEmployee'
											label='Employee'
											onChange={e =>
												handleFilter(
													e,
													'filterEmployee',
												)
											}
											disabled={false}
											checked={displayFilters.employee}
										/>
									</div>
									<div>
										<Checkbox
											name='filterNonEmployee'
											label='Non-Employee'
											onChange={e =>
												handleFilter(
													e,
													'filterNonEmployee',
												)
											}
											disabled={false}
											checked={displayFilters.nonEmployee}
										/>
									</div>
									<div>
										<Checkbox
											name='filterContingentWorker'
											label='Contingent Worker'
											onChange={e =>
												handleFilter(
													e,
													'filterContingentWorker',
												)
											}
											disabled={false}
											checked={
												displayFilters.contingentWorker
											}
										/>
									</div>
									<div>
										<Checkbox
											name='filterExpired'
											label='Expired'
											onChange={e =>
												handleFilter(e, 'filterExpired')
											}
											disabled={false}
											checked={displayFilters.expired}
										/>
									</div>
									<div>
										<Checkbox
											name='filterExpiringSoon'
											label={`Expiring in next ${expiringSoonThreshold} days`}
											onChange={e =>
												handleFilter(
													e,
													'filterExpiringSoon',
												)
											}
											disabled={false}
											checked={
												displayFilters.expiringSoon
											}
										/>
									</div>
								</div>
							</div>
						</Accordion>
					) : null}
					<div
						id='resultsControls'
						className={classes['results-row']}
					>
						{bulkSelections.length > 0 ? (
							<div
								id='selectionSummary'
								className={classes['summary']}
							>
								<strong>Selection Summary: </strong>{' '}
								{bulkSelections.length} user(s) selected
								<div>
									{bulkSelections
										.map(
											staff =>
												staff.firstName +
												' ' +
												(staff.middleInitial.length > 0
													? staff.middleInitial + ' '
													: '') +
												staff.lastName,
										)
										.join(', ')}
								</div>
							</div>
						) : null}
					</div>
					<div id='list' className={listContainerStyle}>
						{staffList && staffList.length > 0 ? (
							<div className={classes['user-row']}>
								<div className={classes['select-all']}>
									<div
										className={
											classes['user-detail-select']
										}
									>
										<Button
											key={'selectAll'}
											variant='outlined'
											disabled={false}
											onClick={e => {
												selectAllBulkSelection(
													e,
													bulkSelections.length === 0,
												);
											}}
										>
											{(bulkSelections.length > 0
												? 'Des'
												: 'S') + 'elect All'}
										</Button>
									</div>
								</div>
							</div>
						) : null}
					</div>
					<div id='list' className={listContainerStyle}>
						{staffList}
					</div>
				</section>
				<section id='bulkActions' className={classes['bulk-container']}>
					{bulkButtons.map(bulkButton => (
						<Link
							to={{ pathname: bulkButton.path }}
							onClick={e => {
								if (bulkSelections.length === 0) {
									e.preventDefault();
								}
							}}
							className={concatenateClassNames(
								['button__link--reset'],
								classes,
							)}
							key={'bulk-action-' + bulkButton.label}
						>
							<Button disabled={bulkSelections.length === 0}>
								{bulkButton.label}
							</Button>
						</Link>
					))}
				</section>
			</div>
		</UARLayout>
	);
};

export default Dashboard;
